import { call, put, takeLatest } from 'redux-saga/effects';
import { CANCEL } from 'redux-saga';
import * as T from './voiceAgentConstants';
import * as actions from './voiceAgentActions';
import {
  getVoiceAgentSummary,
  getVoiceAgentTopItems,
  getVoiceAgentTimeSaved,
  getVoiceAgentOrdersByDay,
  getVoiceAgentOrders,
  getVoiceAgentConversionFunnel,
  getVoiceAgentCallActivityLog,
  getVoiceAgentCallActivityLogOutcomes,
  getVoiceAgentCallTranscript,
  getVoiceAgentCallVolumeByHour,
  getVoiceAgentCallVolumeByDay,
  getVoiceAgentOutcomeDistribution,
  getVoiceAgentRevenueKpi,
  getVoiceAgentTopRevenueItems,
  getVoiceAgentTransfers,
  getVoiceAgentTransferReasons,
} from '../../../services/voiceAgentApi';

/* Every fetcher above takes an optional trailing `AbortSignal`. This wraps a
   call to one of them with its own AbortController and stamps the returned
   promise with redux-saga's CANCEL symbol — so when `takeLatest` cancels a
   stale task (e.g. a rapid date-filter change, or React StrictMode's
   dev-only double-invoke of mount effects), the in-flight fetch is actually
   aborted instead of completing in the background and just having its
   result discarded. Without this, the network tab shows two requests per
   dispatch even though only one ever reaches the reducer. */
function abortable<T>(fn: (...args: any[]) => Promise<T>, ...args: any[]): Promise<T> {
  const controller = new AbortController();
  const promise: any = fn(...args, controller.signal);
  promise[CANCEL] = () => controller.abort();
  return promise;
}

function* fetchVoiceAgentSummarySaga(action: ReturnType<typeof actions.fetchVoiceAgentSummaryRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentSummary, { locationId, from, to });
    yield put(actions.fetchVoiceAgentSummarySuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentSummaryFailure(error?.message || 'Failed to load Voice Agent summary.'));
  }
}

function* fetchVoiceAgentTopItemsSaga(action: ReturnType<typeof actions.fetchVoiceAgentTopItemsRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentTopItems, { locationId, from, to });
    yield put(actions.fetchVoiceAgentTopItemsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentTopItemsFailure(error?.message || 'Failed to load top items.'));
  }
}

function* fetchVoiceAgentTimeSavedSaga(action: ReturnType<typeof actions.fetchVoiceAgentTimeSavedRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentTimeSaved, { locationId, from, to });
    yield put(actions.fetchVoiceAgentTimeSavedSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentTimeSavedFailure(error?.message || 'Failed to load time saved.'));
  }
}

function* fetchVoiceAgentOrdersByDaySaga(action: ReturnType<typeof actions.fetchVoiceAgentOrdersByDayRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentOrdersByDay, { locationId, from, to });
    yield put(actions.fetchVoiceAgentOrdersByDaySuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentOrdersByDayFailure(error?.message || 'Failed to load orders by day.'));
  }
}

function* fetchVoiceAgentOrdersSaga(action: ReturnType<typeof actions.fetchVoiceAgentOrdersRequest>) {
  try {
    const { locationId, from, to, page, limit } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentOrders, { locationId, from, to }, page, limit);
    yield put(actions.fetchVoiceAgentOrdersSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentOrdersFailure(error?.message || 'Failed to load recent voice orders.'));
  }
}

function* fetchVoiceAgentConversionFunnelSaga(action: ReturnType<typeof actions.fetchVoiceAgentConversionFunnelRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentConversionFunnel, { locationId, from, to });
    yield put(actions.fetchVoiceAgentConversionFunnelSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentConversionFunnelFailure(error?.message || 'Failed to load conversion funnel.'));
  }
}

function* fetchVoiceAgentCallActivityLogSaga(action: ReturnType<typeof actions.fetchVoiceAgentCallActivityLogRequest>) {
  try {
    const { locationId, from, to, outcome, sortBy, sortOrder, page, limit } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentCallActivityLog, { locationId, from, to }, { outcome, sortBy, sortOrder, page, limit });
    yield put(actions.fetchVoiceAgentCallActivityLogSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentCallActivityLogFailure(error?.message || 'Failed to load call activity log.'));
  }
}

function* fetchVoiceAgentCallActivityLogOutcomesSaga(action: ReturnType<typeof actions.fetchVoiceAgentCallActivityLogOutcomesRequest>) {
  try {
    const { locationId } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentCallActivityLogOutcomes, locationId);
    yield put(actions.fetchVoiceAgentCallActivityLogOutcomesSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentCallActivityLogOutcomesFailure(error?.message || 'Failed to load outcome options.'));
  }
}

function* fetchVoiceAgentCallTranscriptSaga(action: ReturnType<typeof actions.fetchVoiceAgentCallTranscriptRequest>) {
  try {
    const { callSid } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentCallTranscript, callSid);
    yield put(actions.fetchVoiceAgentCallTranscriptSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentCallTranscriptFailure(error?.message || 'Failed to load call transcript.'));
  }
}

function* fetchVoiceAgentCallVolumeByHourSaga(action: ReturnType<typeof actions.fetchVoiceAgentCallVolumeByHourRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentCallVolumeByHour, { locationId, from, to });
    yield put(actions.fetchVoiceAgentCallVolumeByHourSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentCallVolumeByHourFailure(error?.message || 'Failed to load call volume by hour.'));
  }
}

function* fetchVoiceAgentCallVolumeByDaySaga(action: ReturnType<typeof actions.fetchVoiceAgentCallVolumeByDayRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentCallVolumeByDay, { locationId, from, to });
    yield put(actions.fetchVoiceAgentCallVolumeByDaySuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentCallVolumeByDayFailure(error?.message || 'Failed to load call volume by day.'));
  }
}

function* fetchVoiceAgentOutcomeDistributionSaga(action: ReturnType<typeof actions.fetchVoiceAgentOutcomeDistributionRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentOutcomeDistribution, { locationId, from, to });
    yield put(actions.fetchVoiceAgentOutcomeDistributionSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentOutcomeDistributionFailure(error?.message || 'Failed to load outcome distribution.'));
  }
}

function* fetchVoiceAgentRevenueKpiSaga(action: ReturnType<typeof actions.fetchVoiceAgentRevenueKpiRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentRevenueKpi, { locationId, from, to });
    yield put(actions.fetchVoiceAgentRevenueKpiSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentRevenueKpiFailure(error?.message || 'Failed to load voice revenue KPIs.'));
  }
}

function* fetchVoiceAgentTopRevenueItemsSaga(action: ReturnType<typeof actions.fetchVoiceAgentTopRevenueItemsRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentTopRevenueItems, { locationId, from, to });
    yield put(actions.fetchVoiceAgentTopRevenueItemsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentTopRevenueItemsFailure(error?.message || 'Failed to load top revenue items.'));
  }
}

function* fetchVoiceAgentTransfersSaga(action: ReturnType<typeof actions.fetchVoiceAgentTransfersRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentTransfers, { locationId, from, to });
    yield put(actions.fetchVoiceAgentTransfersSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentTransfersFailure(error?.message || 'Failed to load transfers.'));
  }
}

function* fetchVoiceAgentTransferReasonsSaga(action: ReturnType<typeof actions.fetchVoiceAgentTransferReasonsRequest>) {
  try {
    const { locationId, from, to } = action.payload;
    const response: any = yield call(abortable, getVoiceAgentTransferReasons, { locationId, from, to });
    yield put(actions.fetchVoiceAgentTransferReasonsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchVoiceAgentTransferReasonsFailure(error?.message || 'Failed to load transfer reasons.'));
  }
}

export default function* voiceAgentSaga() {
  yield takeLatest(T.FETCH_VOICE_AGENT_SUMMARY_REQUEST, fetchVoiceAgentSummarySaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_TOP_ITEMS_REQUEST, fetchVoiceAgentTopItemsSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_TIME_SAVED_REQUEST, fetchVoiceAgentTimeSavedSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_ORDERS_BY_DAY_REQUEST, fetchVoiceAgentOrdersByDaySaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_ORDERS_REQUEST, fetchVoiceAgentOrdersSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_CONVERSION_FUNNEL_REQUEST, fetchVoiceAgentConversionFunnelSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_REQUEST, fetchVoiceAgentCallActivityLogSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_CALL_ACTIVITY_LOG_OUTCOMES_REQUEST, fetchVoiceAgentCallActivityLogOutcomesSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_CALL_TRANSCRIPT_REQUEST, fetchVoiceAgentCallTranscriptSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_HOUR_REQUEST, fetchVoiceAgentCallVolumeByHourSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_CALL_VOLUME_BY_DAY_REQUEST, fetchVoiceAgentCallVolumeByDaySaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_OUTCOME_DISTRIBUTION_REQUEST, fetchVoiceAgentOutcomeDistributionSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_REVENUE_KPI_REQUEST, fetchVoiceAgentRevenueKpiSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_TOP_REVENUE_ITEMS_REQUEST, fetchVoiceAgentTopRevenueItemsSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_TRANSFERS_REQUEST, fetchVoiceAgentTransfersSaga);
  yield takeLatest(T.FETCH_VOICE_AGENT_TRANSFER_REASONS_REQUEST, fetchVoiceAgentTransferReasonsSaga);
}
